import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as FileSaver from 'file-saver';
import { Subject, forkJoin } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { UserTab } from "../../../auth/models";
import { AuthService } from "../../../auth/services";
import { FeesHelper } from "../../../common/helpers/fees/fees.helper";
import { Currency, Units } from "../../../common/models";
import { CurrencyRatesService, UnitRatesService } from "../../../common/services";
import { FormulaConfig, FormulaParserService } from '../../../common/services/formula-parser.service';

import { PlattsPricesService } from "../../../indexes/services";
import { indexRateKey } from "../../../indexes/services/platts/platts-prices.service";
import { BidRateTables, formulaWeightToVolume, loadBidRateTables, tableUsgPerMt } from "../../../common/helpers/unit/bid-unit-rates";
import { ToastService } from "../../../main/services";
import { ROUND_SUPPLIER_STATUSES } from "../../models";
import { ContractOffers } from "../../models/contract-offers/contract-offers";
import { Supplier } from "../../models/supplier/supplier";
import { LocationTagsService } from "../../services/location-tags/location-tags.service";
import {
  TenderBidAnalysisReportService
} from "../../services/tender-bid-analysis-report/tender-bid-analysis-report.service";
import { TenderSuppliersService } from "../../services/tender-suppliers/tender-suppliers.service";

@Component({
    selector: 'jft-bid-analysis-report',
    templateUrl: './bid-analysis-report.component.html',
    styleUrls: ['./bid-analysis-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})


export class BidAnalysisReportComponent implements OnInit {
    public totalPageItems: number;
    public currentPage: number = 1;
    public itemsPerPage: number = 20;
    public hasMore: boolean = true;
    public loadingMore: boolean = false;
    public Math = Math;
    public unitRates;
    // Per-bid tables (decisions #46, #47): each bid at its own rate, else the tender's, else 331.
    public rateTables: BidRateTables | null = null;
    public currencyRates;
    public baseCurrency: Currency;
    public baseUnit: Units;
    public tenderSettings;
    public dateRange: string = '12_month';
    public baseExchangeRateDate: any = null;
    public dateFormat = 'YYYY-MM-DD';
    public currentDateTime: Date = new Date();
    tender: any;
    loading = false;
    public contracts: ContractOffers[];
    public OFFER_STATUSES = ROUND_SUPPLIER_STATUSES
    public bidPrices = {};
    public collapsedNotes: { [key: string]: boolean } = {};
    public selectedLocationTags: number[] = [];
    public locationTagOptions: UserTab[] = [];
    public searchText: string = '';
    public selectedSuppliers: number[] = [];
    public supplierOptions: Supplier[] = [];
    public brandedFilter: boolean | null = null;
    public companyOptions: { name: string, value: string }[] = [];
    public selectedCompanies: string[] = [];
    public showBranded: boolean = false;
    public showFuelType: boolean = false;
    private searchSubject: Subject<string> = new Subject();
    private activeFormulas: Record<string, FormulaConfig> = {};

    constructor(private route: ActivatedRoute,
                private toast: ToastService,
                private currencyRatesService: CurrencyRatesService,
                private unitRatesService: UnitRatesService,
                private plattsPricesService: PlattsPricesService,
                private service: TenderBidAnalysisReportService,
                private ngbModal: NgbModal,
                private injector: Injector,
                private router: Router,
                private locationTagsService: LocationTagsService,
                private tenderSuppliersService: TenderSuppliersService,
                private formulaParser: FormulaParserService,
                private authService: AuthService) {}

    ngOnInit(): void {
        let data = this.route.snapshot.data;
        // Walk up via pathFromRoot because `reports` is a non-empty-path parent,
        // so resolved `tender` data doesn't auto-inherit into this child snapshot.
        this.tender = this.route.snapshot.pathFromRoot
            .map((r) => r.data['tender'])
            .find((t) => t);
        this.contracts = this.route.snapshot.data['contract_offers'] || [];
        this.totalPageItems = this.contracts.length; // Initial load from resolver
        this.hasMore = this.totalPageItems >= this.itemsPerPage;
        this.tenderSettings = data['tenderSettings'];
        this.unitRates = data['unitRates'];

        this.baseCurrency = this.tenderSettings.currency;
        this.baseUnit = this.tenderSettings.units;
        this.initBaseExchangeRateDate();
        this.showBranded = !this.tender.company.settings.isHiddenField('location_branded', undefined, 'airline');
        this.showFuelType = !this.tender.company.settings.isHiddenField('location_fuel_type', undefined, 'airline');

        // Load all active formulas from the user's company settings (singleton, always up-to-date)
        const user = this.authService.getCurrentUser();
        const customFields = user?.company?.settings?.custom_fields;
        this.activeFormulas = FormulaParserService.getActiveFormulas(
            customFields?.formulas,
            customFields?.deleted_formulas,
        );
        this.loadLocationTags();
        this.loadSuppliers();
        this.loadCompanyOptions();

        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(searchText => {
            this.searchText = searchText;
            this.onFilterChange();
        });
        // Don't call pageChanged() here - let loadLocationTags() handle it
        // This ensures saved tags are loaded before fetching data

        // Sort contracts by IATA code when data is loaded
        if (this.contracts && Array.isArray(this.contracts)) {
            this.contracts.sort((a, b) => {
                const valueA = a.location?.iata || '';
                const valueB = b.location?.iata || '';
                return valueA.localeCompare(valueB);
            });
        }
    }

    public pageChanged(page = 1, append = false) {
        if (!append) {
            this.contracts = [];
            this.currentPage = 1;
        } else {
            this.loadingMore = true;
        }

        this.service.fetch(this.tender.id, page, this.itemsPerPage, this.selectedLocationTags, this.searchText, this.selectedSuppliers, this.brandedFilter, this.selectedCompanies).subscribe(
            data => {
                if (append) {
                    this.contracts = [...this.contracts, ...data];
                } else {
                    this.contracts = data;
                }
                this.initUpdates();
                this.totalPageItems = this.service.getTotalPageItems();
                this.hasMore = this.contracts.length < this.totalPageItems;
                this.loadingMore = false;
            },
            error => {
                this.loadingMore = false;
                this.toast.error('Log could not be retrieved');
            }
        );
    }

    public onSearch(event: any) {
        this.searchSubject.next(event.target.value);
    }

    public onSupplierFilterChange() {
        this.onFilterChange();
    }

    public onFilterChange() {
        this.currentPage = 1;
        this.pageChanged();
    }

    private loadSuppliers() {
        this.tenderSuppliersService.fetch(this.tender.id, true).subscribe(
            suppliers => {
                this.supplierOptions = suppliers;
            },
            error => console.error('Failed to load suppliers:', error)
        );
    }

    private loadCompanyOptions() {
        this.service.fetchCompanyOptions(this.tender.id).subscribe(
            options => this.companyOptions = options,
            error => { this.companyOptions = []; }
        );
    }

    public onCompanyFilterChange() {
        this.currentPage = 1;
        this.pageChanged();
    }

    public loadMore() {
        if (this.loadingMore || !this.hasMore) return;
        this.currentPage++;
        this.pageChanged(this.currentPage, true);
    }

    public onItemsPerPageChange() {
        this.currentPage = 1; // Reset to first page when changing items per page
        this.pageChanged(1);
    }

    private initBaseExchangeRateDate() {
        if (this.tenderSettings.exchangeRateDate && this.tenderSettings.isExchangeRateFixed) {
            this.baseExchangeRateDate = this.tenderSettings.exchangeRateDate.format(this.dateFormat);
        }
    }

    public initUpdates(){
        this.getCurrencyRates();
    }

    public getCurrencyRates() {
        this.currencyRatesService.fetch(this.baseCurrency.code, {
            tender: this.tender.id,
            date: this.baseExchangeRateDate
        }, false).subscribe((currencies: []) => {
            this.currencyRates = currencies;
            loadBidRateTables(this.unitRatesService, this.unitRates, this.allBids()).subscribe(tables => {
                this.rateTables = tables;
                this.insertAverageIndexPrice();
            });
        })
    }

    /** The tables one bid converts with; the tender's until the per-bid ones are loaded. */
    public tablesOf(bid) {
        return this.rateTables ? this.rateTables.ofBid(bid) : { jet: this.unitRates, saf: this.unitRates, safFee: this.unitRates };
    }

    private allBids(): any[] {
        const bids = [];
        (this.contracts || []).forEach(contract => [contract.offers, contract.currentOffers, contract.mockOffers]
            .forEach(list => (list || []).forEach(offer => offer?.bid && bids.push(offer.bid))));
        return bids;
    }

    public calcTotalFees(bid) {
        return FeesHelper.calcTotalFees(bid, this.currencyRates, this.baseCurrency.code, this.tablesOf(bid).jet, this.baseUnit.code);
    }

    public toCurrencyToUnit(value: number, currency: string, unit: string, currencyRates: {}, baseCurrencyCode: string, unitRates: {}, baseUnitCode: string) {
        let val = FeesHelper.convertToCurrency(currency, value, currencyRates, baseCurrencyCode);
        val = FeesHelper.convertToUnit(unit, val, unitRates, baseUnitCode);
        return val ? val.toFixed(6) : val;
    }

    public getObsAverageIndexPrice(offer, params, contract) {
        return this.plattsPricesService.fetchPriceAverage(offer.bid.index.index_code.id,
            offer.bid.index.bate ? offer.bid.index.bate.id : '', contract.dateRange ? contract.dateRange : this.dateRange,
            { ...params, wToVolume: indexRateKey(offer.bid) });
    }

    private insertAverageIndexPrice() {
        let params = {
            'currencyId': this.tenderSettings.currency.id,
            'unitId': this.tenderSettings.units.id,
            'dateRange': this.dateRange,
            'tenderId': this.tender.id,
        };

        // Collect all offers (regular, current, mock) with their cache keys
        const allOfferEntries: { offer: any, key: string }[] = [];
        const uniqueKeys: Map<string, { indexId: any, bateCode: any, dateRange: string, wToVolume: string }> = new Map();

        const collectOffers = (offerList: any[], contract: any) => {
            offerList.forEach(offer => {
                if (offer.bid && offer.bid.index && (offer.bid.index.index_code.provider == 'platts' || offer.bid.index.index_code.provider == 'argus' || offer.bid.index.index_code.provider == 'opis')) {
                    const indexId = offer.bid.index.index_code.id;
                    const bateCode = offer.bid.index.bate ? offer.bid.index.bate.id : '';
                    const dr = contract.dateRange ? contract.dateRange : this.dateRange;
                    // Per bid rate too: an MT-quoted index averages differently per weight to volume.
                    const wToVolume = indexRateKey(offer.bid);
                    const key = `${indexId}_${bateCode}_${dr}_${wToVolume}`;
                    uniqueKeys.set(key, { indexId, bateCode, dateRange: dr, wToVolume });
                    allOfferEntries.push({ offer, key });
                }
            });
        };

        this.contracts.forEach(contract => {
            collectOffers(contract.offers || [], contract);
            collectOffers(contract.currentOffers || [], contract);
            collectOffers(contract.mockOffers || [], contract);
        });

        if (uniqueKeys.size === 0) {
            this.updateOffers();
            return;
        }

        // Make one API call per unique (indexId, bateCode, dateRange) combination
        const keys = Array.from(uniqueKeys.keys());
        const uniqueObs = keys.map(key => {
            const { indexId, bateCode, dateRange, wToVolume } = uniqueKeys.get(key);
            return this.plattsPricesService.fetchPriceAverage(indexId, bateCode, dateRange, { ...params, wToVolume });
        });

        forkJoin(uniqueObs).subscribe((data) => {
            const resultsByKey: { [key: string]: any } = {};
            keys.forEach((key, i) => resultsByKey[key] = data[i]);

            allOfferEntries.forEach(({ offer, key }) => {
                this.bidPrices[offer.bid.id] = resultsByKey[key];
            });
            this.updateOffers();
        });
    }

    public updateOffers() {
        this.contracts.forEach(contract => {
            if (contract.offers) {
                this._updateOffers(contract.offers, contract);
            }
            if (contract.currentOffers) {
                this._updateOffers(contract.currentOffers, contract);
            }
            if (contract.mockOffers) {
                this._updateOffers(contract.mockOffers, contract);
                // Tag mock offers so the template can display "(Mock)" label
                contract.mockOffers.forEach(offer => offer['_isMock'] = true);
            }
            // Merge regular offers + mock offers into a single sorted list
            const allOffers = [...(contract.offers || []), ...(contract.mockOffers || [])];
            contract['allBids'] = allOffers.sort((a, b) => a.bid['total_field'] - b.bid['total_field']);
        })
    }

    private _updateOffers(offers: any[], contract?: any) {
        offers.forEach(offer => {
            let diffValue: any = 0;
            let waccValue: any = 0;
            let safAmountValue: any = 0;
            let bid = offer.bid;
            let averagePrice = this.bidPrices[offer.bid.id] || 0;
            const tables = this.tablesOf(bid);

            if (bid.differential && bid.differential.price && bid.differential.price.currency && bid.differential.units) {
                diffValue = Number(this.toCurrencyToUnit(
                    bid.differential.price.value,
                    bid.differential.price.currency.code,
                    bid.differential.units.code,
                    this.currencyRates,
                    this.baseCurrency.code,
                    tables.jet,
                    this.baseUnit.code
                ));
                offer.bid['exchanged_differential'] = diffValue
            }

            if (bid.safFeeAmountUnits && bid.safFeeAmountUnits.price && bid.safFeeAmountUnits.price.currency && bid.safFeeAmountUnits.units) {
                safAmountValue = Number(this.toCurrencyToUnit(
                    bid.safFeeAmountUnits.price.value,
                    bid.safFeeAmountUnits.price.currency.code,
                    bid.safFeeAmountUnits.units.code,
                    this.currencyRates,
                    this.baseCurrency.code,
                    tables.safFee,
                    this.baseUnit.code
                ));
                offer.bid['exchanged_saf_fee_amount_units'] = safAmountValue
            }
            if (bid.volume_based_fees) {
                bid['volume_fees_total'] = Number(this.calcTotalFees(bid));
                bid['volume_fees_all'] = FeesHelper.calcTotalFeesAll(bid, this.currencyRates, this.baseCurrency.code, tables.jet, this.baseUnit.code);
            }
            if (bid.perUsageFees) {
                bid['per_uplift_fees_total'] = Number(FeesHelper.calcTotalPerFees(bid, this.currencyRates, this.baseCurrency.code, this.unitRates));
                bid['per_uplift_fees_all'] = Number(FeesHelper.calcTotalPerFeesAll(bid, this.currencyRates, this.baseCurrency.code));
            }
            let diffPlusVbfees = diffValue + (bid['volume_fees_total'] ? bid['volume_fees_total'] : 0);
            if (bid['index']) {
                 bid['index']['average_price'] = averagePrice;
            }

            // Compute market price value for WACC and formula context
            let marketValueForWacc = 0;
            if (bid.marketPrice && bid.marketPrice.price) {
                marketValueForWacc = Number(this.toCurrencyToUnit(
                    bid.marketPrice.price['price']['value'],
                    bid.marketPrice.price['price'].currency.code,
                    bid.marketPrice.price['units'].code,
                    this.currencyRates,
                    this.baseCurrency.code,
                    tables.jet,
                    this.baseUnit.code
                )) || 0;
            }

            // Compute WACC
            let preWaccTotal = parseFloat(diffPlusVbfees) + parseFloat(String(marketValueForWacc)) + parseFloat(averagePrice) + parseFloat(safAmountValue);
            let paymentTerms = bid.payment?.open_invoice?.payment_terms || 0;
            // Deduct transit time from payment terms (if set)
            if (bid.transitTime) {
                paymentTerms = Math.max(0, paymentTerms - bid.transitTime);
            }
            let costOfCapital = this.tenderSettings?.costOfCapital || 0;
            let waccComputed = 0;
            if (costOfCapital && paymentTerms) {
                waccComputed = ((costOfCapital / 100) / 365) * paymentTerms * preWaccTotal * -1;
            }
            offer.bid['exchanged_wacc'] = parseFloat(waccComputed.toFixed(4));

            // Evaluate total_field using formula engine
            const formulaContext: Record<string, number> = {
                differential: parseFloat(diffValue) || 0,
                volume_fees_total: bid['volume_fees_total'] || 0,
                saf_fee_amount: parseFloat(safAmountValue) || 0,
                per_uplift_fees_total: bid['per_uplift_fees_total'] || 0,
                index_value: parseFloat(averagePrice) || 0,
                market_price_value: marketValueForWacc,
                weight_to_volume: formulaWeightToVolume(bid, tableUsgPerMt(this.unitRates), this.rateTables?.limits),
                payment_terms: paymentTerms,
                cost_of_capital: costOfCapital,
                wacc: offer.bid['exchanged_wacc'],
                revised_differential: 0,
                is_market_price: bid.marketPrice ? 1 : 0,
                average_uplift: Number(contract?.averageUplift) || 0,
                volume_per_uplift: (contract?.volume && contract?.annualFrequency) ? Number(contract.volume) / Number(contract.annualFrequency) : 0,
                contract_volume: Number(contract?.volume) || 0,
                annual_frequency: Number(contract?.annualFrequency) || 0,
                months_of_service: Number(contract?.monthService) || 0,
                monthly_volume: contract?.monthService ? (Number(contract?.volume) || 0) / Number(contract.monthService) : 0,
                available_volume: Number(bid.fuelAvailabilityPercentage) || 0,
                saf_mandated_price: 0,
                saf_voluntary_price: 0,
                saf_blend_percentage: Number(bid.safMandateBlend) || 0,
                volume_fees_all: bid['volume_fees_all'] || 0,
                per_uplift_fees_all: bid['per_uplift_fees_all'] || 0,
            };
            // Evaluate all active formulas
            for (const [formulaKey, formulaConfig] of Object.entries(this.activeFormulas)) {
                try {
                    bid[formulaKey] = parseFloat(this.formulaParser.evaluate(formulaConfig.ast, formulaContext).toFixed(4));
                } catch (e) {
                    bid[formulaKey] = 0;
                }
            }
            // Backward-compatible aliases
            bid['total_field'] = bid['total_price'] || 0;
            bid['diff_plus_vbfees'] = bid['diff_vol_fees'] || 0;
            bid['diff_plus_vbfees_saf'] = bid['diff_saf_vol_fees'] || 0;
            bid['diff_plus_vbfees_saf_hock'] = bid['diff_saf_vol_event_fees'] || 0;
            bid['diff_plus_vbfees_hock'] = bid['diff_vol_event_fees'] || 0;
        })
        offers = offers.sort((a,b) => a.bid['total_field'] - b.bid['total_field'])
    }

    public downloadAsPDF(): void {
        // Use totalPageItems (total location count) instead of contracts.length (current page only)
        const totalLocations = this.totalPageItems || 0;

        // Get location tag filter string
        const locationTagFilter = this.selectedLocationTags.length > 0
            ? this.selectedLocationTags.join(',')
            : undefined;

        console.log('Total locations:', totalLocations);
        console.log('Contracts on current page:', this.contracts?.length);
        console.log('Location tag filter:', locationTagFilter);

        // For large reports (>10 locations), always use async email delivery
        if (totalLocations > 10){
            console.log('Opening modal for large report');
            this.openPdfModal();
            return;
        }

        // For small reports (≤10 locations), direct download
        this.loading = true;
        this.service.getPdf(this.tender.id, locationTagFilter, this.searchText, this.selectedSuppliers, this.brandedFilter, this.selectedCompanies).subscribe(
            data => {
                FileSaver.saveAs(data, `bid_analysis_report_${this.tender.name}.pdf`);
                this.loading = false;
            },
            error => {
                this.loading = false;
                console.log('Error response:', error);
                // If backend returns 400 (too large), show modal as fallback
                if (error.status === 400 && error.error?.requires_async) {
                    console.log('Backend says report too large, opening modal');
                    this.openPdfModal();
                } else {
                    this.toast.error('Error generating PDF report');
                }
            }
        );
    }

    public redirectSelectContract(event){
        this.router.navigate(['/tenders/'+this.tender.id+'/analytics-settings/tender-current-contract']);
    }

    public openPdfModal() {
        // Get location tag filter string
        const locationTagFilter = this.selectedLocationTags.length > 0
            ? this.selectedLocationTags.join(',')
            : undefined;

        // Show initial toast that file is being prepared
        this.toast.info('Preparing Bid Analysis Report in background. You will be notified when ready.');
        this.loading = true;

        this.service.getPdfDelayed(this.tender.id, locationTagFilter, this.searchText, this.selectedSuppliers, this.brandedFilter, this.selectedCompanies).subscribe(
            (response: any) => {
                this.loading = false;
                // The notification system will show a toast when file is ready
            },
            error => {
                this.loading = false;
                this.toast.error('Error requesting PDF report. Please try again.');
            }
        );
    }

    public toggleNoteCollapse(noteId: string): void {
        this.collapsedNotes[noteId] = !this.collapsedNotes[noteId];
    }

    public isNoteCollapsed(noteId: string): boolean {
        return this.collapsedNotes[noteId] || false;
    }

    public shouldTruncateNote(note: string): boolean {
        return note && note.length > 255;
    }

    public getTruncatedNote(note: string): string {
        if (!note) return '';
        return note.length > 255 ? note.substring(0, 255) + '...' : note;
    }

    public getNoteId(offer: any, section: string): string {
        return `${section}_${offer.bid?.id || offer.id}_note`;
    }

    /** The bid's invoice frequency label; the frequency lives on the payment (common 0027-0029). */
    public invoiceFrequencyLabel(offer: any): string {
        return this.tender.company.settings.getFilteredOptionLabel('frequency_base', offer.bid?.payment?.invoice_frequency);
    }

    public getAvgMethodCode(averagingMethod: any, company: any): string {
        if (!averagingMethod) return '';

        // If the object already has col1 (e.g. if we somehow synced it), use it
        if (averagingMethod.col1) return averagingMethod.col1;

        let label = '';
        if (company && company.settings) {
            // This retrieves the custom label from company settings (e.g. "P W - Weekly...")
            label = company.settings.getFilteredOptionLabel('averaging_method', averagingMethod, 'code');
        }

        // Fallback to name/code if no custom label found
        if (!label) {
            label = averagingMethod.name || averagingMethod.code || '';
        }

        // Apply splitting logic: "P W - Weekly" -> "P W"
        if (label && typeof label === 'string' && label.includes(' - ')) {
            return label.split(' - ')[0];
        }

        return label;
    }

    // Location tags methods
    private loadLocationTags() {
        this.locationTagsService.fetchLocationTags().subscribe(
            (tags: UserTab[]) => {
                this.locationTagOptions = tags;
                // Load previously selected tags from localStorage and validate them
                const savedTags = this.locationTagsService.loadSelectedTags();
                this.selectedLocationTags = this.locationTagsService.validateSelectedTags(savedTags, tags);
                // Always fetch data after tags are loaded (whether tags exist or not)
                this.pageChanged();
            },
            (error) => {
                console.error('Failed to load location tags:', error);
                this.selectedLocationTags = [];
                // Even on error, fetch data without tag filter
                this.pageChanged();
            }
        );
    }

    public onLocationTagFilterChange() {
        // Save selected tags to localStorage for this user
        this.locationTagsService.saveSelectedTags(this.selectedLocationTags);
        this.currentPage = 1;
        this.pageChanged();
    }
}
